import { useState, useEffect } from "react";
import arrayUnion from "array-union";
import { db } from "./Firebase";
import { toast } from "react-toastify";
import { useGetAllUsers } from "./User";

const createCompany = (name, manager) =>
  new Promise((resolve, reject) => {
    db.collection("companies")
      .add({
        name,
        companyManagers: manager ? [db.collection("users").doc(manager)] : [],
        fleetManagers: [],
        drivers: [],
        stats: {
          averageEnergyLevel: 50,
          averageTimeMostFatigued: "13:30"
        },
        settings: {
          notificationThreshold: {
            companyManager: 20,
            fleetManager: 35,
            driver: 50
          }
        }
      })
      .then(() => {
        toast("Company Created", { type: toast.TYPE.INFO });
        resolve(`Created fleet`);
      })
      .catch(error => {
        // Failed to create company within Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const getCompany = companyId =>
  new Promise((resolve, reject) => {
    db.collection("companies")
      .doc(companyId)
      .get()
      .then(company => resolve(company.data()))
      .catch(error => {
        // Failed to fetch company from Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const updateCompany = (companyId, name) =>
  new Promise((resolve, reject) => {
    const companyRef = db.collection("companies").doc(companyId);
    companyRef
      .set(
        {
          name
        },
        { merge: true }
      )
      .then(() => {
        toast("Company Updated", { type: toast.TYPE.INFO });
        resolve(`Created fleet`);
      })
      .catch(error => {
        // Failed to update Company record.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const updateCompanyThresholdValues = (companyId, managerThreshold, fleetThreshold, driverThreshold) =>
  new Promise((resolve, reject) => {
    const companyRef = db.collection("companies").doc(companyId);
    companyRef
      .set(
        {
          settings: {
            notificationThreshold: {
              companyManager: Number(managerThreshold),
              fleetManager: Number(fleetThreshold),
              driver: Number(driverThreshold)
            }
          }
        },
        { merge: true }
      )
      .then(() => toast("Energy Threshold Updated", { type: toast.TYPE.INFO }))
      .catch(error => {
        // Failed to update Company record.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const updateCompanyDriverRefs = (companyId, drivers) =>
  new Promise((resolve, reject) => {
    const companyRef = db.collection("companies").doc(companyId);
    companyRef
      .set({
        drivers
      })
      .then(company => resolve(company))
      .catch(error => {
        // Failed to update Company record.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const updateCompanyFleetManagerRefs = (companyId, fleetManagers) =>
  new Promise((resolve, reject) => {
    const companyRef = db.collection("companies").doc(companyId);
    companyRef
      .set({
        fleetManagers
      })
      .then(company => resolve(company))
      .catch(error => {
        // Failed to update Company record.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const updateCompanyManagerRefs = (companyId, companyManagers) =>
  new Promise((resolve, reject) => {
    const companyRef = db.collection("companies").doc(companyId);
    companyRef
      .get()
      .then(company => {
        const companyInfo = company.data();
        const mergedCompanyManagers = arrayUnion(companyInfo.companyManagers, companyManagers);
        return mergedCompanyManagers;
      })
      .then(mergedCompanyManagers => {
        companyRef
          .set({
            companyManagers: mergedCompanyManagers
          })
          .then(company => resolve(company))
          .catch(error => {
            // Failed to update Company record.
            const errorCode = error.code;
            const errorMessage = error.message;
            reject(new Error(`${errorMessage} (${errorCode})`));
          });
      });
  });

const getAllCompanies = cid =>
  new Promise((resolve, reject) => {
    let companiesRef = db.collection("companies");

    if (cid) {
      companiesRef = companiesRef.doc(cid);
    }

    companiesRef
      .get()
      .then(companies => {
        const allCompanies = [];

        if (companies.docs) {
          companies.docs.map((company, index) => {
            const companyInfo = company.data();
            companyInfo.cid = company.id;
            allCompanies.push(companyInfo);

            if (companies.docs.length === index + 1) return resolve(allCompanies);
          });
        } else {
          const companyInfo = companies.data();
          companyInfo.cid = companies.id;
          allCompanies.push(companyInfo);

          if (allCompanies.length === 1) return resolve(allCompanies);
        }
      })
      .catch(error => {
        // Failed to fetch company from Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const useGetCompany = cid => {
  const [company, setCompany] = useState({
    loading: true,
    company: null
  });

  const companyRef = db.collection("companies").doc(cid);

  useEffect(() => {
    companyRef.onSnapshot(comp => {
      const companyInfo = comp.data();
      companyInfo.cid = comp.id;

      setCompany({
        loading: false,
        company: companyInfo
      });
    });
  }, []);

  return company;
};

const useGetGlobalSettings = cid => {
  const [globalSettings, setGlobalSettings] = useState({ globalSettings: {} });

  const settingsRef = db.collection("global").doc("settings");

  useEffect(() => {
    settingsRef.onSnapshot(settings => {
      setGlobalSettings({ globalSettings: settings.data() });
    });
  }, []);

  return globalSettings;
};

const updateDriverThresholdValue = driverThreshold =>
  new Promise((resolve, reject) => {
    const settingsRef = db.collection("global").doc("settings");
    settingsRef
      .set(
        {
          driverNotificationThreshold: Number(driverThreshold)
        },
        { merge: true }
      )
      .then(() => toast("Energy Threshold Updated", { type: toast.TYPE.INFO }))
      .catch(error => {
        // Failed to update Company record.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

const useGetAllCompanies = cid => {
  const [allCompanies, setAllCompanies] = useState({
    loading: true,
    companies: null
  });

  const companiesRef = db.collection("companies");

  // if (cid) {
  //   companiesRef = companiesRef.limit(limit);
  // }

  // if (limit) {
  //   companiesRef = companiesRef.limit(limit);
  // }

  useEffect(() => {
    companiesRef.onSnapshot(companies => {
      const allCompanyDocs = [];

      companies.docs.map((company, index) => {
        const companyInfo = company.data();
        companyInfo.cid = company.id;
        allCompanyDocs.push(companyInfo);

        if (companies.docs.length === index + 1) {
          return setAllCompanies({
            loading: false,
            companies: allCompanyDocs
          });
        }
      });
    });
  }, []);

  return allCompanies;
};

const deleteCompany = companyId =>
  new Promise((resolve, reject) => {
    db.collection("companies")
      .doc(companyId)
      .delete()
      .then(() => {
        toast("Company Deleted", { type: toast.TYPE.INFO });
        resolve(`Deleted company ${companyId}`);
      })
      .catch(error => {
        // Failed to delete company from Firebase.
        const errorCode = error.code;
        const errorMessage = error.message;
        reject(new Error(`${errorMessage} (${errorCode})`));
      });
  });

export {
  getCompany,
  getAllCompanies,
  createCompany,
  deleteCompany,
  updateCompany,
  updateCompanyThresholdValues,
  updateCompanyManagerRefs,
  useGetAllCompanies,
  useGetCompany,
  useGetGlobalSettings,
  updateDriverThresholdValue
};
